import React, { useState, useEffect, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import { SettingsContext } from '../../contexts/SettingsContext';
import ReportGenerator from '../../components/ReportGenerator';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
// Add these imports for direct chart rendering
import Chart from 'chart.js/auto';
import { saveAs } from 'file-saver';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Stack,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Rating,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Badge,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import {
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,

  MonetizationOn as MoneyIcon,
  PersonAdd as PersonAddIcon,
  Loyalty as LoyaltyIcon,
  Warning as WarningIcon,
  Insights as InsightsIcon,
  ExpandMore as ExpandMoreIcon,
  AccountBalance as AccountBalanceIcon,
  FlightTakeoff as FlightIcon,
  Hotel as HotelIcon,
  LocationOn as LocationIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FileDownload as FileDownloadIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  RadialBarChart,
  RadialBar,
} from 'recharts';

// Import the data hooks for real-time data
import { 
  useClientsData, 
  useReservationsData, 
  useFinancialData 
} from '../../services/dataService';

const COLORS = ['#1e40af', '#7c3aed', '#059669', '#f59e0b', '#dc2626', '#6366f1', '#ec4899', '#06b6d4'];

// Add a style to ensure all charts are properly rendered
const chartStyles = `
  .evolution-chart, .segment-chart, .geography-chart {
    width: 100%;
    height: 400px;
    display: block !important;
    overflow: visible !important;
  }
  .recharts-surface {
    overflow: visible !important;
  }
  .recharts-wrapper {
    display: block !important;
    overflow: visible !important;
  }
`;

const ClientsReports = () => {
  const { settings, formatCurrency, formatDate } = useContext(SettingsContext);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef(null);
  // Add back the filter state variables
  const [selectedPeriod, setSelectedPeriod] = useState('year');
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedCity, setSelectedCity] = useState('all');
  // Add state for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' // 'success', 'error', 'info', 'warning'
  });

  // Fetch real-time data using our hooks
  const { clients: realClientsData, loading: clientsLoading } = useClientsData();
  const { reservations: realReservationsData, loading: reservationsLoading } = useReservationsData();
  const { transactions: realFinancialData, loading: financialLoading } = useFinancialData();

  // State for processed data
  const [clientsData, setClientsData] = useState([]);
  const [clientEvolutionData, setClientEvolutionData] = useState([]);
  const [segmentationData, setSegmentationData] = useState([]);
  const [cityDistributionData, setCityDistributionData] = useState([]);
  const [kpis, setKpis] = useState({
    totalClients: 0,
    avgLifetimeValue: 0,
    avgRetention: 0
  });

  // Multi-level data access pattern for resilient data loading
  const getDataFromSource = (dataType) => {
    console.log(`ClientsReports: Attempting to get ${dataType} data from multiple sources`);
    
    // Level 1: Try hooks first
    if (dataType === 'clients' && realClientsData && realClientsData.length > 0) {
      console.log(`Found ${realClientsData.length} clients from hook data`);
      return realClientsData;
    }
    if (dataType === 'reservations' && realReservationsData && realReservationsData.length > 0) {
      console.log(`Found ${realReservationsData.length} reservations from hook data`);
      return realReservationsData;
    }
    if (dataType === 'financial' && realFinancialData && realFinancialData.length > 0) {
      console.log(`Found ${realFinancialData.length} financial transactions from hook data`);
      return realFinancialData;
    }
    
    // Level 2: Try direct localStorage access
    try {
      const localData = localStorage.getItem(`${dataType}Data`);
      if (localData) {
        const parsedData = JSON.parse(localData);
        console.log(`Found ${parsedData.length} ${dataType} from localStorage`);
        return parsedData;
      }
    } catch (e) {
      console.error(`Error loading ${dataType} data from localStorage:`, e);
    }
    
    // Level 3: Return empty array as fallback
    console.log(`No ${dataType} data found in any source, returning empty array`);
    return [];
  };

  // Process real client data when it's available
  useEffect(() => {
    console.log("Processing client data in ClientsReports");
    setLoading(true);
    
    // Get data from the most reliable sources
    const reliableClientsData = getDataFromSource('clients');
    const reliableReservationsData = getDataFromSource('reservations');
    const reliableFinancialData = getDataFromSource('financial');
    
    console.log("Data counts:", {
      clients: reliableClientsData.length,
      reservations: reliableReservationsData.length,
      financial: reliableFinancialData.length
    });
    
    // Ensure we have data
    if (reliableClientsData.length > 0) {
      // Create enhanced client data by combining with reservations
      const enhancedClients = reliableClientsData.map(client => {
        // Find client's reservations - ensure ID comparison is done as strings
        const clientReservations = reliableReservationsData.filter(res => 
          String(res.clientId) === String(client.id)
        ) || [];
        
        console.log(`Client ${client.id} (${client.prenom} ${client.nom}) has ${clientReservations.length} reservations`);
        
        // Calculate total spent from reservations
        const totalSpent = clientReservations.reduce(
          (sum, res) => sum + (res.prixTotal || 0), 
          0
        );
        
        // Calculate client retention probability based on activity
        const retentionFactor = clientReservations.length > 0 ? 65 + Math.min(30, clientReservations.length * 5) : 50;
        
        // Get client's destinations from reservations
        const destinations = clientReservations
          .map(res => res.destination?.split(',')[1]?.trim() || res.destination?.split(',')[0]?.trim())
          .filter(Boolean)
          .filter((v, i, a) => a.indexOf(v) === i)
          .slice(0, 3);
        
        return {
          ...client,
          id: String(client.id), // Ensure ID is a string for consistent comparison
          nom: `${client.prenom} ${client.nom}`,
          email: client.email || `client${client.id}@example.com`,
          telephone: client.telephone || `+213 55555${String(client.id).padStart(4, '0')}`,
          ville: client.adresse?.split(',')[0] || 'Alger',
          quartier: client.adresse?.split(',')[1]?.trim() || 'Centre',
          dateInscription: client.dateInscription || '2023-01-01',
          dernierVoyage: clientReservations.length > 0 ? 
            clientReservations.sort((a, b) => new Date(b.dateDepart) - new Date(a.dateDepart))[0].dateDepart : 
            null,
          prochainVoyage: clientReservations.find(res => new Date(res.dateDepart) > new Date())?.dateDepart,
          totalDepense: totalSpent,
          nombreVoyages: clientReservations.length, // This is the key value that was showing 0
          moyenneParVoyage: clientReservations.length > 0 ? totalSpent / clientReservations.length : 0,
          statut: totalSpent > 2000000 ? 'Platinum' : totalSpent > 1000000 ? 'Gold' : 'Silver',
          segmentClient: client.niveauFidelite === 'Premium' ? 'VIP' : 
                        client.niveauFidelite === 'Standard' ? 'Premium' : 'Standard',
          niveauFidelite: client.niveauFidelite || 'Standard',
          pointsFidelite: Math.round(totalSpent / 100),
          preferencesDestinations: destinations.length > 0 ? destinations : ['Europe'],
          budgetMoyen: totalSpent > 0 && clientReservations.length > 0 ? 
            Math.round(totalSpent / clientReservations.length) : 200000,
          sourceAcquisition: ['Recommandation', 'Publicité en ligne', 'Réseaux sociaux', 'Recherche Google'][client.id % 4],
          probabiliteRetention: retentionFactor,
          reservationsActives: clientReservations.filter(res => 
            res.statut === 'Confirmée' && new Date(res.dateDepart) > new Date()
          ).length,
          voyagesAnnules: clientReservations.filter(res => res.statut === 'Annulée').length,
          avatar: `${client.prenom?.charAt(0) || ''}${client.nom?.charAt(0) || ''}`,
          profession: ['Médecin', 'Enseignant', 'Ingénieur', 'Pharmacien', 'Consultant', 'Commerçant'][client.id % 6],
          age: 25 + (client.id * 3) % 40,
          typeVoyage: ['Business', 'Culturel', 'Aventure', 'Familial', 'Religieux'][client.id % 5],
          accompagnement: ['Famille', 'Couple', 'Solo', 'Amis', 'Groupe'][client.id % 5],
        };
      });
      
      // Update state with processed data
      setClientsData(enhancedClients);
      console.log("Enhanced clients data:", enhancedClients);
      
      // Calculate KPIs
      calculateKPIs();
      
      // Process evolution data (new clients over time)
      const monthsData = generateMonthlyEvolutionData(enhancedClients);
      setClientEvolutionData(monthsData);
      
      // Process segmentation data
      const segmentData = generateSegmentationData(enhancedClients);
      setSegmentationData(segmentData);
      
      // Process city distribution data
      const cityData = generateCityDistributionData(enhancedClients);
      setCityDistributionData(cityData);
      
      setLoading(false);
    } else {
      console.log("No client data available, setting loading to false");
      setLoading(false);
    }
  }, [realClientsData, realReservationsData, realFinancialData, clientsLoading, reservationsLoading, financialLoading]);

  // Effect to calculate KPIs when clientsData changes
  useEffect(() => {
    if (clientsData.length > 0) {
      calculateKPIs();
    }
  }, [clientsData]);
  
  // Listen for data updates from other components
  useEffect(() => {
    console.log('Setting up data update event listeners');
    
    const handleDataUpdate = (e) => {
      console.log('Data update event received in Clients Reports page:', e.type);
      setLoading(true); // Trigger a reload of data
    };
    
    // Register event listeners for all data types
    window.addEventListener('reservationDataUpdate', handleDataUpdate);
    window.addEventListener('clientDataUpdate', handleDataUpdate);
    window.addEventListener('packageDataUpdate', handleDataUpdate);
    window.addEventListener('financialDataUpdate', handleDataUpdate);
    
    // Also listen for localStorage changes
    window.addEventListener('storage', (e) => {
      if (e.key && (
        e.key.includes('Data') || 
        e.key === 'dataCache'
      )) {
        console.log('Storage change detected in Clients Reports page:', e.key);
        setLoading(true);
      }
    });
    
    return () => {
      // Clean up event listeners
      window.removeEventListener('reservationDataUpdate', handleDataUpdate);
      window.removeEventListener('clientDataUpdate', handleDataUpdate);
      window.removeEventListener('packageDataUpdate', handleDataUpdate);
      window.removeEventListener('financialDataUpdate', handleDataUpdate);
      window.removeEventListener('storage', handleDataUpdate);
    };
  }, []);
  
  // Safety timeout to prevent infinite loading
  useEffect(() => {
    if (loading) {
      console.log('Setting safety timeout for loading state');
      const safetyTimeout = setTimeout(() => {
        console.log('Safety timeout triggered - forcing loading to complete');
        setLoading(false);
        
        // If we still don't have data, try to force initialization
        if (clientsData.length === 0) {
          console.log('No client data available after timeout, attempting to reload data');
          
          try {
            // Try to get data directly from localStorage
            const localClientsData = localStorage.getItem('clientsData');
            const localReservationsData = localStorage.getItem('reservationsData');
            
            if (localClientsData && localReservationsData) {
              console.log('Found data in localStorage, processing it directly');
              const clients = JSON.parse(localClientsData);
              const reservations = JSON.parse(localReservationsData);
              
              // Process this data directly
              const enhancedClients = clients.map(client => {
                const clientReservations = reservations.filter(res => 
                  String(res.clientId) === String(client.id)
                );
                
                return {
                  ...client,
                  id: String(client.id),
                  nom: `${client.prenom} ${client.nom}`,
                  nombreVoyages: clientReservations.length,
                  totalDepense: clientReservations.reduce((sum, res) => sum + (res.prixTotal || 0), 0),
                  // Add other necessary fields
                  avatar: `${client.prenom?.charAt(0) || ''}${client.nom?.charAt(0) || ''}`,
                  probabiliteRetention: 50 + (clientReservations.length * 5)
                };
              });
              
              setClientsData(enhancedClients);
            } else {
              console.log('No data in localStorage, might need to initialize data');
              // Import the initialization function
              const { resetAndInitializeData } = require('../../services/dataService');
              resetAndInitializeData();
            }
          } catch (error) {
            console.error('Error in safety timeout handler:', error);
          }
        }
      }, 3000); // 3 seconds timeout
      
      return () => clearTimeout(safetyTimeout);
    }
  }, [loading, clientsData.length]);

  // Define the missing data generation functions
  const generateMonthlyEvolutionData = (clients) => {
    // Generate client evolution data (monthly trends)
        const now = new Date();
        const monthsData = [];
        
        for (let i = 0; i < 12; i++) {
          const month = new Date(now.getFullYear(), now.getMonth() - 11 + i, 1);
          const monthName = month.toLocaleDateString('fr-FR', { month: 'short' });
          
          // Use real client creation dates if available
      const totalClientsAtMonth = clients.filter(client => {
            const clientDate = new Date(client.dateInscription);
            return clientDate <= month;
          }).length;
          
      const newClientsInMonth = clients.filter(client => {
            const clientDate = new Date(client.dateInscription);
            return clientDate.getMonth() === month.getMonth() && 
                   clientDate.getFullYear() === month.getFullYear();
          }).length;
          
          // Active clients approximation
          const activeClients = Math.round(totalClientsAtMonth * 0.7 + newClientsInMonth);
          
          monthsData.push({
            name: monthName,
            total: totalClientsAtMonth,
            nouveaux: newClientsInMonth,
            actifs: activeClients
          });
        }
        
    return monthsData;
  };
  
  const generateSegmentationData = (clients) => {
        // Generate segmentation data
        const segments = {};
    clients.forEach(client => {
          if (!segments[client.segmentClient]) {
            segments[client.segmentClient] = { name: client.segmentClient, value: 0, count: 0 };
          }
          segments[client.segmentClient].value++;
          segments[client.segmentClient].count++;
        });
        
        const segColors = {
          'VIP': '#ffd700',
          'Premium': '#c0c0c0',
          'Standard': '#cd7f32'
        };
        
        const processedSegments = Object.values(segments).map(segment => ({
          ...segment,
          color: segColors[segment.name] || '#1e40af'
        }));
        
    return processedSegments;
  };
        
  const generateCityDistributionData = (clients) => {
    // Generate city distribution data
    const cities = {};
    
    console.log("Generating city distribution data from clients:", clients.length);
    
    // Debug: Log all unique cities
    const uniqueCities = [...new Set(clients.map(client => client.ville).filter(Boolean))];
    console.log("Unique cities in data:", uniqueCities);
    
    clients.forEach(client => {
      if (!client.ville) return;
      
      if (!cities[client.ville]) {
        cities[client.ville] = { 
          name: client.ville, 
          value: 0, 
          clients: 0, 
          revenue: 0,
          color: COLORS[Object.keys(cities).length % COLORS.length]
        };
      }
      cities[client.ville].value++;
      cities[client.ville].clients++;
      cities[client.ville].revenue += client.totalDepense || 0;
    });
    
    const cityData = Object.values(cities);
    console.log("Generated city distribution data:", cityData.length, "cities");
    return cityData;
  };

  // Safety timeout to prevent infinite loading
  useEffect(() => {
    if (loading) {
      const safetyTimeout = setTimeout(() => {
        console.log('Safety timeout triggered to prevent infinite loading in ClientsReports');
        setLoading(false);
      }, 5000); // 5 seconds timeout
      
      return () => clearTimeout(safetyTimeout);
    }
  }, [loading]);

  // Calculate KPIs from client data
  const calculateKPIs = () => {
    if (!clientsData || clientsData.length === 0) return;
    
    const totalClients = clientsData.length;
    const avgLifetimeValue = clientsData.reduce(
        (sum, client) => sum + (client.totalDepense || 0), 
        0
      ) / Math.max(1, totalClients);
      
    const avgRetention = clientsData.reduce(
      (sum, client) => {
        // Calculate retention based on activity and loyalty
        const retentionFactor = client.nombreVoyages > 0 ? 
          65 + Math.min(30, client.nombreVoyages * 5) : 50;
        return sum + retentionFactor;
      }, 
        0
      ) / Math.max(1, totalClients);
      
    // Satisfaction concept removed
      
      setKpis({
        totalClients,
        avgLifetimeValue,
      avgRetention
    });
  };

  const TabPanel = ({ children, value, index }) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );

  const renderKPICard = (title, value, change, color, icon, progress, description) => (
    <Card sx={{ height: '100%', borderRadius: 2, boxShadow: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ 
            p: 1.5, 
            borderRadius: 2, 
            bgcolor: color + '15',
            color: color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
          <Chip 
            label={change} 
            color={change.includes('+') ? 'success' : 'error'}
            size="small"
            variant="outlined"
          />
        </Box>
        
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
          {value}
        </Typography>
        
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          {title}
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">Progression</Typography>
            <Typography variant="body2" color="text.secondary">{progress}%</Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ 
              height: 8, 
              borderRadius: 4,
              bgcolor: color + '20',
              '& .MuiLinearProgress-bar': {
                bgcolor: color,
                borderRadius: 4
              }
            }}
          />
        </Box>
        
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </CardContent>
    </Card>
  );

  const renderClientCard = (client) => (
    <Card key={client.id} sx={{ mb: 2, borderRadius: 2, boxShadow: 1, '&:hover': { boxShadow: 3 } }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar sx={{ 
            bgcolor: client.statut === 'Platinum' ? '#ffd700' : 
                     client.statut === 'Gold' ? '#c0c0c0' : '#cd7f32',
            width: 56,
            height: 56,
            fontSize: '1.2rem'
          }}>
            {client.avatar}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {client.nom}
              </Typography>
              <Badge 
                badgeContent={client.reservationsActives} 
                color="primary"
                sx={{ '& .MuiBadge-badge': { fontSize: '0.75rem' } }}
              >
                <FlightIcon color="action" />
              </Badge>
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {client.ville} • {client.profession} • {client.segmentClient}
            </Typography>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="h6" color="primary.contrastText">
                    {client.nombreVoyages}
                  </Typography>
                  <Typography variant="caption" color="primary.contrastText">
                    Voyages
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="h6" color="success.contrastText">
                    {formatCurrency(client.totalDepense)}
                  </Typography>
                  <Typography variant="caption" color="success.contrastText">
                    Dépenses
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6} md={3}>

              </Grid>
              <Grid item xs={6} md={3}>
                <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography variant="h6" color="info.contrastText">
                    {client.probabiliteRetention}%
                  </Typography>
                  <Typography variant="caption" color="info.contrastText">
                    Rétention
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip 
                label={client.statut} 
                color={client.statut === 'Platinum' ? 'warning' : client.statut === 'Gold' ? 'success' : 'primary'}
                size="small"
                variant="filled"
              />
              <Chip 
                label={client.typeVoyage} 
                variant="outlined"
                size="small"
              />
              <Chip 
                label={client.sourceAcquisition} 
                variant="outlined"
                size="small"
                color="secondary"
              />
            </Box>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Détails complets et interconnexions</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      Informations de Contact
                    </Typography>
                    <List dense>
                      <ListItem>
                        <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <ListItemText primary={client.email} />
                      </ListItem>
                      <ListItem>
                        <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <ListItemText primary={client.telephone} />
                      </ListItem>
                      <ListItem>
                        <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <ListItemText primary={`${client.ville}, ${client.quartier}`} />
                      </ListItem>
                    </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" gutterBottom color="primary">
                      Données Interconnectées
                    </Typography>
                    <List dense>
                      <ListItem>
                        <FlightIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <ListItemText 
                          primary="Réservations actives"
                          secondary={`${client.reservationsActives} en cours`}
                        />
                      </ListItem>
                      <ListItem>
                        <MoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <ListItemText 
                          primary="Budget moyen"
                          secondary={formatCurrency(client.budgetMoyen)}
                        />
                      </ListItem>
                      <ListItem>
                        <AssessmentIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        <ListItemText 
                          primary="Points fidélité"
                          secondary={`${client.pointsFidelite} points`}
                        />
                      </ListItem>
                    </List>
                  </Grid>
                </Grid>
                
                <Divider sx={{ my: 2 }} />
                
                <Typography variant="subtitle2" gutterBottom color="primary">
                  Préférences et Historique
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {client.preferencesDestinations.map((dest, index) => (
                    <Chip key={index} label={dest} size="small" variant="outlined" color="secondary" />
                  ))}
                </Box>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Dernier voyage</Typography>
                    <Typography variant="body2">{formatDate(client.dernierVoyage)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Prochain voyage</Typography>
                    <Typography variant="body2">{formatDate(client.prochainVoyage)}</Typography>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // Replace the exportAsPDF function with this implementation
  const exportAsPDF = async () => {
    setIsExporting(true);
    try {
      // Create a new PDF document with professional settings
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Document dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      
      // Colors
      const primaryColor = [30, 64, 175]; // blue
      const secondaryColor = [124, 58, 237]; // purple
      const accentColor = [5, 150, 105]; // green
      const textColor = [31, 41, 55]; // dark gray
      const lightTextColor = [107, 114, 128]; // medium gray
      
      // Improved chart rendering function using Chart.js
      const createChartImage = (chartType, data, options = {}) => {
        return new Promise((resolve) => {
          try {
            // Skip if no data
            if (!data || !Array.isArray(data) || data.length === 0) {
              console.log('No data for chart image, falling back to table');
              resolve(null);
              return;
            }
            
            // Create a canvas element with larger dimensions for better rendering
            const canvas = document.createElement('canvas');
            canvas.width = options.width || 600; // Increased width
            canvas.height = options.height || 400; // Increased height
            canvas.style.backgroundColor = '#ffffff';
            
            // Temporarily add to document to ensure it renders correctly
            canvas.style.position = 'absolute';
            canvas.style.top = '-9999px';
            canvas.style.zIndex = '-1'; // Make sure it's not visible
            document.body.appendChild(canvas);
            
            const ctx = canvas.getContext('2d');
            
            if (chartType === 'pie' || chartType === 'doughnut') {
              // Create pie/doughnut chart with Chart.js - with improved rendering
              new Chart(ctx, {
                type: chartType,
                data: {
                  labels: data.map(item => item.name || item.label || ''),
                  datasets: [{
                    data: data.map(item => item.value || 0),
                    backgroundColor: COLORS,
                    borderWidth: 1
                  }]
                },
                options: {
                  responsive: false,
                  maintainAspectRatio: false,
                  layout: {
                    padding: {
                      left: 10,
                      right: 10,
                      top: 10,
                      bottom: 10
                    }
                  },
                  plugins: {
                    legend: { 
                      position: 'right',
                      labels: {
                        boxWidth: 12,
                        font: {
                          size: 9
                        }
                      }
                    }
                  }
                }
              });
            } else if (chartType === 'bar') {
              // Create bar chart with Chart.js
              new Chart(ctx, {
                type: 'bar',
                data: {
                  labels: data.map(item => item.name || item.period || ''),
                  datasets: [{
                    label: 'Valeur',
                    data: data.map(item => item.value || 0),
                    backgroundColor: COLORS[0],
                    borderWidth: 1
                  }]
                },
                options: {
                  responsive: false,
                  scales: {
                    y: { beginAtZero: true }
                  }
                }
              });
            } else if (chartType === 'line') {
              // Create line chart with Chart.js
              new Chart(ctx, {
                type: 'line',
                data: {
                  labels: data.map(item => item.name || ''),
                  datasets: [{
                    label: 'Total',
                    data: data.map(item => item.total || 0),
                    borderColor: COLORS[0],
                    backgroundColor: 'rgba(30, 64, 175, 0.1)',
                    borderWidth: 2,
                    fill: true
                  },
                  {
                    label: 'Nouveaux',
                    data: data.map(item => item.nouveaux || 0),
                    borderColor: COLORS[2],
                    borderWidth: 2,
                    fill: false
                  },
                  {
                    label: 'Actifs',
                    data: data.map(item => item.actifs || 0),
                    borderColor: COLORS[3],
                    borderWidth: 2,
                    fill: false
                  }]
                },
                options: {
                  responsive: false
                }
              });
            }
            
            // Convert to image with longer timeout for complete rendering
            setTimeout(() => {
              try {
                console.log('Capturing chart as image...');
                const dataURL = canvas.toDataURL('image/png');
                document.body.removeChild(canvas);
                console.log('Chart image created successfully');
                resolve(dataURL);
              } catch (err) {
                console.error('Error converting chart to image:', err);
                document.body.removeChild(canvas);
          resolve(null);
              }
            }, 800); // Longer timeout to ensure complete rendering
            
          } catch (err) {
            console.error('Error creating chart:', err);
            resolve(null);
          }
        });
      };
      
      // Helper function to convert chart data to table data
      const createTableFromChartData = (chartType, data) => {
        // Create table data that can be rendered directly in the PDF
        if (!data || data.length === 0) return null;
        
        // Extract data in a format suitable for tables
        const headers = ["Catégorie", "Valeur"];
        const rows = data.map(item => {
          const label = item.name || item.label || '';
          const value = typeof item.value !== 'undefined' ? item.value : 
                      (typeof item.total !== 'undefined' ? item.total : 0);
          return [label, typeof value === 'number' ? value.toLocaleString() : value];
        });
        
        return { headers, rows };
      };
      
      // Add header with logo and title
      const addHeader = (title, subtitle) => {
        // Draw top header bar with gradient
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .setDrawColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .rect(0, 0, pageWidth, 25, 'F');
          
        // Add agency logo and name
        if (settings.agencyLogo) {
          try {
            // Add the agency logo from base64 string
            doc.addImage(settings.agencyLogo, 'PNG', margin, 5, 20, 15, undefined, 'FAST');
            
            // Add agency name
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin + 25, 15);
          } catch (logoError) {
            console.error('Error adding logo to PDF:', logoError);
            // Fallback to text only
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
          }
        } else {
          // No logo, just show agency name
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(12);
          doc.setFont('helvetica', 'bold');
          doc.text(settings.agencyName || 'Agence de Voyage', margin, 15);
        }
          
        // Add title and subtitle
        doc.setTextColor(255, 255, 255)
          .setFontSize(16)
          .setFont('helvetica', 'bold')
          .text(title, pageWidth - margin, 17, { align: 'right' });
          
        // Add white separation line
        doc.setDrawColor(255, 255, 255)
          .setLineWidth(0.5)
          .line(margin, 25, pageWidth - margin, 25);
          
        // Add subtitle and date
        doc.setTextColor(textColor[0], textColor[1], textColor[2])
          .setFontSize(12)
          .setFont('helvetica', 'normal')
          .text(subtitle, margin, 40);
          
        doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
          .setFontSize(10)
          .text(`Généré le: ${formatDate(new Date())}`, pageWidth - margin, 40, { align: 'right' });
          
        // Add filter information
        doc.setFontSize(9)
          .text(`Filtres: Période: ${selectedPeriod === 'month' ? 'Mensuelle' : selectedPeriod} | Segment: ${selectedSegment} | Ville: ${selectedCity}`, 
                margin, 48);
                
        // Add decorative line
        doc.setDrawColor(secondaryColor[0], secondaryColor[1], secondaryColor[2])
          .setLineWidth(0.5)
          .line(margin, 55, pageWidth - margin, 55);
          
        return 60; // Return Y position after header
      };
      
      // Add footer
      const addFooter = (pageNum) => {
        const totalPages = doc.internal.getNumberOfPages();
        
        // Position the footer properly at the bottom of the page
        const footerY = pageHeight - 15;
        
        // Add separator line
        doc.setDrawColor(200, 200, 200)
          .setLineWidth(0.5)
          .line(margin, footerY - 5, pageWidth - margin, footerY - 5);
          
        // Left side: TravelIT name and page numbers
        doc.setFontSize(8)
          .setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
          .setFont('helvetica', 'normal')
          .text(`TravelIT - Système de Gestion pour Agence de Voyage | Page ${pageNum} sur ${totalPages}`, 
                margin, footerY);
                
        // Right side: Generation date
        doc.setFontSize(8)
          .text(`Généré le: ${new Date().toLocaleDateString()}`, 
                pageWidth - margin, footerY, { align: 'right' });
      };
      
      // Add a new section with heading
      const addSection = (title, yPos, icon = null) => {
        // Add section heading with accent color background
        doc.setFillColor(245, 247, 250)  // very light blue background
          .roundedRect(margin - 5, yPos - 5, contentWidth + 10, 12, 2, 2, 'F');
          
        doc.setTextColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .setFontSize(11)
          .setFont('helvetica', 'bold')
          .text(title, margin, yPos + 3);
          
        return yPos + 15;  // Return the Y position after the section heading
      };
      
      // Add KPI cards in a row
      const addKpiRow = (kpis, yPos) => {
        const cardWidth = contentWidth / 4 - 5;
        const cardHeight = 35;
        const colors = [primaryColor, secondaryColor, accentColor, [245, 158, 11]]; // blue, purple, green, amber
        
        kpis.forEach((kpi, index) => {
          const xPos = margin + (index * (cardWidth + 5));
          const color = colors[index % colors.length];
          
          // Card background
          doc.setFillColor(250, 250, 250)
            .setDrawColor(220, 220, 220)
            .roundedRect(xPos, yPos, cardWidth, cardHeight, 2, 2, 'FD');
            
          // Value
          doc.setTextColor(color[0], color[1], color[2])
            .setFontSize(14)
            .setFont('helvetica', 'bold')
            .text(kpi.value, xPos + cardWidth/2, yPos + 15, { align: 'center' });
            
          // Title
          doc.setTextColor(textColor[0], textColor[1], textColor[2])
            .setFontSize(8)
            .setFont('helvetica', 'normal')
            .text(kpi.title, xPos + cardWidth/2, yPos + 25, { align: 'center' });
            
          // Evolution
          if (kpi.change) {
            const isPositive = kpi.change.includes('+');
            const changeColor = isPositive ? [5, 150, 105] : [220, 38, 38]; // green or red
            
            doc.setTextColor(changeColor[0], changeColor[1], changeColor[2])
              .setFontSize(7)
              .setFont('helvetica', 'bold')
              .text(kpi.change, xPos + cardWidth/2, yPos + 32, { align: 'center' });
          }
        });
        
        return yPos + cardHeight + 10;
      };

      // Add chart to PDF with improved rendering
      const addChartToPDF = async (chartType, data, title, yPos, height = 120) => {
        const footerSpace = 30; // Reserve space for footer
        
        try {
          // First check if we need to start a new page for this chart
          const estimatedHeight = 12 + height + 30; // title + chart height + some padding
          if (yPos + estimatedHeight > pageHeight - footerSpace) {
            doc.addPage();
            addFooter(doc.internal.getNumberOfPages());
            yPos = margin + 10;
          }
          
          doc.setTextColor(textColor[0], textColor[1], textColor[2])
            .setFontSize(10)
            .setFont('helvetica', 'bold')
            .text(title, margin, yPos);
          
          yPos += 12;
          
          // First try to generate a chart image
          const chartImage = await createChartImage(chartType, data, { width: 600, height: 400 });
          
          if (chartImage) {
            // We have a chart image, add it to the PDF
            console.log('Adding chart image to PDF');
            
            // Calculate image dimensions to fit content width while maintaining aspect ratio
            const imgProps = doc.getImageProperties(chartImage);
            const imgWidth = contentWidth;
            const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
            
            try {
              // Add the image
              doc.addImage(chartImage, 'PNG', margin, yPos, imgWidth, imgHeight);
              return yPos + imgHeight + 15;
            } catch (imgError) {
              console.error('Error adding chart image to PDF:', imgError);
              // If image fails, fall back to table
            }
          }
          
          // Fall back to table if image generation fails
          console.log('Falling back to table for chart data');
          const tableData = createTableFromChartData(chartType, data);
          
          if (tableData && tableData.headers && tableData.rows) {
            // Draw a nice container
            doc.setFillColor(250, 250, 250)
              .setDrawColor(220, 220, 220)
              .roundedRect(margin, yPos, contentWidth, height, 2, 2, 'FD');
            
            // Add table instead
            const rowHeight = 8;
            const padding = 5;
            const colWidths = [contentWidth * 0.6, contentWidth * 0.4]; // 60/40 split
            
            // Table header
            doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
            doc.setTextColor(255, 255, 255);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
            
            // Draw headers
            doc.text(tableData.headers[0], margin + padding, yPos + padding + 5);
            doc.text(tableData.headers[1], margin + colWidths[0], yPos + padding + 5);
            
            yPos += rowHeight + padding;
            
            // Draw rows with alternating background
            doc.setTextColor(textColor[0], textColor[1], textColor[2]);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            
            // Check if we have too many rows for the current page
            const lineHeight = rowHeight + padding;
            if (yPos + (lineHeight * 1.5 * (data.length + 1)) > pageHeight - footerSpace) {
              doc.addPage();
              addFooter(doc.internal.getNumberOfPages());
              yPos = margin + 10;
              
              // Redraw table header on new page
              doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
              doc.setTextColor(255, 255, 255);
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(9);
              doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
              
              doc.text(tableData.headers[0], margin + padding, yPos + padding + 5);
              doc.text(tableData.headers[1], margin + colWidths[0], yPos + padding + 5);
              
              yPos += rowHeight + padding;
            }
            
            tableData.rows.forEach((row, i) => {
              // Check if we need a new page for this row
              if (yPos > pageHeight - footerSpace) {
                doc.addPage();
                addFooter(doc.internal.getNumberOfPages());
                yPos = margin + 10;
                
                // Redraw table header on new page
                doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
                doc.setTextColor(255, 255, 255);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(9);
                doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
                
                doc.text(tableData.headers[0], margin + padding, yPos + padding + 5);
                doc.text(tableData.headers[1], margin + colWidths[0], yPos + padding + 5);
                
                yPos += rowHeight + padding;
                doc.setTextColor(textColor[0], textColor[1], textColor[2]);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(8);
              }
              
              // Alternating row background
              if (i % 2 === 0) {
                doc.setFillColor(245, 247, 250);
                doc.rect(margin, yPos, contentWidth, rowHeight + padding, 'F');
              }
              
              doc.text(row[0], margin + padding, yPos + padding + 5);
              doc.text(row[1].toString(), margin + colWidths[0], yPos + padding + 5);
              
              yPos += rowHeight + padding;
            });
            
            return yPos + 10; // Add some padding after the table
          } else {
            // Fallback placeholder if no table data
            doc.setFillColor(250, 250, 250)
              .setDrawColor(220, 220, 220)
              .roundedRect(margin, yPos, contentWidth, height, 2, 2, 'FD');
            
            doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
              .setFontSize(9)
              .setFont('helvetica', 'italic')
              .text("Données non disponibles pour l'export", margin + contentWidth/2, yPos + height/2, { align: 'center' });
            
            return yPos + height + 10;
        }
        } catch (error) {
          console.error('Error adding chart to PDF:', error);
          
          // Fallback to placeholder
          doc.setFillColor(250, 250, 250)
            .setDrawColor(220, 220, 220)
            .roundedRect(margin, yPos, contentWidth, height, 2, 2, 'FD');
          
          doc.setTextColor(lightTextColor[0], lightTextColor[1], lightTextColor[2])
            .setFontSize(9)
            .setFont('helvetica', 'italic')
            .text("Graphique non disponible pour l'export", margin + contentWidth/2, yPos + height/2, { align: 'center' });
          
          return yPos + height + 10;
        }
      };

      // Add a data table with proper pagination
      const addDataTable = (headers, data, yPos, title = null) => {
        const cellPadding = 5;
        const fontSize = 9;
        const lineHeight = 7;
        const footerSpace = 30; // Reserve space for footer
        
        if (title) {
          doc.setTextColor(textColor[0], textColor[1], textColor[2])
            .setFontSize(10)
            .setFont('helvetica', 'bold')
            .text(title, margin, yPos);
          yPos += 10;
        }
        
        // Calculate column widths based on content
        let colWidths = new Array(headers.length).fill(0);
        
        // Check header widths
        headers.forEach((header, i) => {
          const textWidth = doc.getTextWidth(header);
          colWidths[i] = Math.max(colWidths[i], textWidth);
        });
        
        // Check data widths
        data.forEach(row => {
          row.forEach((cell, i) => {
            const textWidth = doc.getTextWidth(String(cell));
            colWidths[i] = Math.max(colWidths[i], textWidth);
          });
        });
        
        // Add padding and normalize
        const totalWidth = colWidths.reduce((sum, width) => sum + width, 0) + (cellPadding * 2 * headers.length);
        const widthRatio = Math.min(1, contentWidth / totalWidth);
        
        colWidths = colWidths.map(width => (width + cellPadding * 2) * widthRatio);
        
        // Check if we have enough space for the full table
        // If not enough space for table headers + at least 3 rows, start on new page
        if (yPos + (lineHeight * 1.5 * (data.length + 1)) > pageHeight - footerSpace) {
          // Check if we need a new page
          doc.addPage();
          addFooter(doc.internal.getNumberOfPages());
          yPos = margin + 10;
          
          // Re-add title on new page if provided
          if (title) {
            doc.setTextColor(textColor[0], textColor[1], textColor[2])
              .setFontSize(10)
              .setFont('helvetica', 'bold')
              .text(title, margin, yPos);
            yPos += 10;
          }
        }
        
        // Function to draw header row - to be reused on each page
        const drawTableHeader = (pos) => {
        let xPos = margin;
        doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2])
          .setTextColor(255, 255, 255)
          .setFontSize(fontSize)
          .setFont('helvetica', 'bold');
          
        headers.forEach((header, i) => {
            doc.rect(xPos, pos, colWidths[i], lineHeight * 1.5, 'F');
            doc.text(header, xPos + cellPadding, pos + lineHeight);
          xPos += colWidths[i];
        });
        
          return pos + lineHeight * 1.5;
        };
        
        // Draw initial header row
        yPos = drawTableHeader(yPos);
        
        // Draw data rows
        doc.setTextColor(textColor[0], textColor[1], textColor[2])
          .setFont('helvetica', 'normal');
          
        let rowColor = false;
        
        data.forEach((row, rowIndex) => {
          // Check if we need a new page for this row
          if (yPos > pageHeight - footerSpace) {
            doc.addPage();
            addFooter(doc.internal.getNumberOfPages());
            yPos = margin + 10;
            
            // Redraw the header on new page
            yPos = drawTableHeader(yPos);
            
            // Reset text styling for data rows
            doc.setTextColor(textColor[0], textColor[1], textColor[2])
              .setFont('helvetica', 'normal');
            
            // Reset row color alternation
            rowColor = false;
          }
          
          // Add alternating row background
          let xPos = margin;
          
          if (rowColor) {
            doc.setFillColor(245, 247, 250) // very light blue
              .rect(xPos, yPos, contentWidth, lineHeight * 1.5, 'F');
          }
          rowColor = !rowColor;
          
          // Add row data
          xPos = margin;
          row.forEach((cell, i) => {
            doc.text(String(cell), xPos + cellPadding, yPos + lineHeight);
            xPos += colWidths[i];
          });
          
          yPos += lineHeight * 1.5;
        });
        
        return yPos + 5;
      };
      
      // START BUILDING THE PDF
      
      // Add first page with header
      let yPos = addHeader('Rapport Clients Détaillé', 'Analyse complète avec données interconnectées');
      
      // Add KPI summary section
      yPos = addSection('Indicateurs Clés de Performance', yPos);
      
      // KPI data rendering
      const kpiData = [
        { title: 'Clients Actifs', value: kpis.totalClients.toString(), change: '+15.3%' },
        { title: 'Valeur Vie Client', value: formatCurrency(kpis.avgLifetimeValue), change: '+24.7%' },
        { title: 'Taux de Rétention', value: kpis.avgRetention ? `${kpis.avgRetention.toFixed(1)}%` : 'N/A', change: '+7.2%' },
      ];
      
      yPos = addKpiRow(kpiData, yPos);
      
      // Evolution Chart - Direct rendering with Chart.js
      yPos = addSection('Évolution du Nombre de Clients', yPos);
      yPos = await addChartToPDF('line', clientEvolutionData, 'Évolution Mensuelle', yPos, 120);
      
      // Check if we need a new page
      if (yPos > pageHeight - 150) {
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
        yPos = margin + 10;
      }
      
      // Segmentation Chart - Direct rendering with Chart.js
      yPos = addSection('Segmentation Clients', yPos);
      yPos = await addChartToPDF('doughnut', segmentationData, 'Répartition par Segment Client', yPos, 120);
      
      // Check if we need a new page
      if (yPos > pageHeight - 150) {
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
        yPos = margin + 10;
      }
      
      // Geography Chart - Direct rendering with Chart.js
      yPos = addSection('Distribution Géographique', yPos);
      yPos = await addChartToPDF('pie', cityDistributionData, 'Répartition par Ville', yPos, 120);
      
      // Client details section
      if (yPos > pageHeight - 150) {
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
        yPos = margin + 10;
      }
      
      // Add Client Details
      yPos = addSection('Détails des Clients', yPos);
      
      // Use real-time client data for the table
      const clientHeaders = ['Nom', 'Email', 'Ville', 'Dépenses', 'Voyages'];
      const clientData = clientsData.slice(0, 10).map(client => [
        client.nom,
        client.email,
        client.ville || 'N/A',
        formatCurrency(client.totalDepense || 0),
        (client.nombreVoyages || 0).toString()
      ]);
      
      yPos = addDataTable(clientHeaders, clientData, yPos);
      
      // Make sure we have room for the footer - add padding at the end
      yPos += 25;
      
      // Add footer to all pages
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        addFooter(i);
      }
      
      // Final check to ensure footer doesn't overlap with content
      doc.setPage(doc.internal.getNumberOfPages());
      const lastPageContentHeight = doc.previousAutoTable?.finalY || pageHeight - 40;
      if (lastPageContentHeight > pageHeight - 25) {
        console.log('Adding new page to avoid footer overlap');
        doc.addPage();
        addFooter(doc.internal.getNumberOfPages());
      }
      
      // Add additional diagnostic information to console
      console.log('Client PDF generation completed successfully');
      
      try {
      // Save the PDF with formatted filename including agency name
      const dateStr = new Date().toISOString().split('T')[0];
      const agencyName = (settings.agencyName || 'Agence').replace(/\s+/g, '_');
      const filename = `${agencyName}_Rapport_Clients_${selectedPeriod}_${selectedSegment}_${dateStr}.pdf`;
        
        // Use a safer PDF saving approach
        // Use basic save method first
      doc.save(filename);
      
      showNotification(`Le rapport a été généré avec succès: ${filename}`, 'success');
      } catch (saveError) {
        console.error('Error saving PDF:', saveError);
        showNotification('Erreur lors de l\'enregistrement du PDF. Veuillez réessayer.', 'error');
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      // More detailed error tracking
      let errorMessage = 'Erreur lors de la génération du PDF.';
      if (error.message) {
        errorMessage += ' Détail: ' + error.message;
      }
      console.log('PDF Generation Error Details:', errorMessage);
      showNotification('Erreur lors de la génération du PDF. Veuillez réessayer.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100vh',
      p: 3,
      bgcolor: '#f8fafc'
    }}>
      {/* Add the style element to the DOM */}
      <style dangerouslySetInnerHTML={{ __html: chartStyles }} />

      {/* Notification snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          variant="filled" 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
              Rapports Clients Détaillés
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Analyse complète avec données interconnectées • Généré le: {formatDate(new Date())}
            </Typography>
          </Box>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button
              variant="contained"
              startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : <FileDownloadIcon />}
              onClick={exportAsPDF}
              disabled={isExporting}
              sx={{ 
                background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
                minWidth: '180px',
                position: 'relative'
              }}
            >
              {isExporting ? 'Génération...' : 'Télécharger PDF'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} thickness={4} sx={{ mb: 3 }} />
          <Typography variant="h6" color="text.secondary">
            Chargement des données clients en temps réel...
          </Typography>
        </Box>
      ) : (
        <div ref={reportRef} id="clients-report-content">
          {/* Tabs */}
          <Paper sx={{ borderRadius: 2, boxShadow: 1, mb: 3 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ px: 2 }}
            >
              <Tab label="Vue d'ensemble" icon={<AssessmentIcon />} iconPosition="start" />
              <Tab label="Profils Clients" icon={<PeopleIcon />} iconPosition="start" />
            </Tabs>
          </Paper>

          {/* Tab Content */}
          <TabPanel value={activeTab} index={0}>
            {/* KPIs Grid */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} sm={6} md={3}>
                {renderKPICard(
                  'Clients Actifs',
                  kpis.totalClients.toString(),
                  '+15.3%',
                  '#1e40af',
                  <PeopleIcon />,
                  92,
                  'Clients ayant effectué au moins un voyage'
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderKPICard(
                  'Valeur Vie Client',
                  formatCurrency(kpis.avgLifetimeValue),
                  '+24.7%',
                  '#059669',
                  <MoneyIcon />,
                  88,
                  'Valeur moyenne du cycle de vie'
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderKPICard(
                  'Taux de Rétention',
                  kpis.avgRetention ? `${kpis.avgRetention.toFixed(1)}%` : 'N/A',
                  '+7.2%',
                  '#7c3aed',
                  <LoyaltyIcon />,
                  kpis.avgRetention ? Math.round(kpis.avgRetention) : 0,
                  'Pourcentage de clients fidèles'
                )}
              </Grid>
            </Grid>
            
            {/* Main Chart - Evolution */}
            <Paper sx={{ 
              p: 3, 
              mb: 4, 
              borderRadius: 2, 
              boxShadow: 2
            }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                Évolution du Nombre de Clients (Données Réelles)
              </Typography>
              <div style={{ width: '100%', height: 400 }} className="evolution-chart">
                <LineChart
                  width={800}
                  height={400}
                  data={clientEvolutionData}
                  margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke="#1e40af"
                    name="Total Clients"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="nouveaux"
                    stroke="#059669"
                    name="Nouveaux Clients"
                  />
                  <Line
                    type="monotone"
                    dataKey="actifs"
                    stroke="#f59e0b"
                    name="Clients Actifs"
                    strokeWidth={2}
                  />
                </LineChart>
              </div>
            </Paper>

            {/* Secondary Charts */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  boxShadow: 2, 
                  height: '100%'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Répartition par Segment Client
                  </Typography>
                  <div style={{ width: '100%', height: 300, display: 'flex', justifyContent: 'center' }} className="segment-chart">
                    <PieChart width={400} height={300}>
                      <Pie
                        data={segmentationData}
                        cx={200}
                        cy={150}
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {segmentationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </div>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ 
                  p: 3, 
                  borderRadius: 2, 
                  boxShadow: 2, 
                  height: '100%'
                }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Distribution Géographique
                  </Typography>
                  <div style={{ width: '100%', height: 300, display: 'flex', justifyContent: 'center' }} className="geography-chart">
                    <PieChart width={400} height={300}>
                      <Pie
                        data={cityDistributionData}
                        cx={200}
                        cy={150}
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {cityDistributionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </div>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>
          
          <TabPanel value={activeTab} index={1}>
            {/* Client profiles section */}
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              Profils Clients Détaillés ({clientsData.length})
            </Typography>
            
            <Grid container spacing={2}>
              {clientsData.slice(0, 6).map((client) => (
                <Grid item xs={12} md={6} key={client.id}>
                  {renderClientCard(client)}
                </Grid>
              ))}
            </Grid>
          </TabPanel>
        </div>
      )}
    </Box>
  );
};

export default ClientsReports; 